import React, { FC, lazy } from 'react'
import { useSelector } from 'react-redux'
import Route from '@kaidam/fc-shared/src/frontend/router/Route'
import RouteContext from '@kaidam/fc-shared/src/frontend/router/RouteContext'
import Container from '@mui/material/Container'
import Box from '@mui/material/Box'
import Loading from '../components/system/Loading'

import routerSelectors from '@kaidam/fc-shared/src/frontend/store/selectors/router'

import Layout from '../pages/Layout'
import NotFound from '../pages/NotFound'

import Login from '../pages/Login'
import Register from '../pages/Register'
import Logout from '../pages/Logout'
import ResetPassword from '../pages/ResetPassword'
import Dashboard from '../pages/Dashboard'

import Suspense from '../components/system/Suspense'

const SettingsLayout = lazy(() => import(/* webpackChunkName: "settings" */ '../pages/settings/Layout'))
const SettingsPassword = lazy(() => import(/* webpackChunkName: "settings" */ '../pages/settings/Password'))
const SettingsProject = lazy(() => import(/* webpackChunkName: "settings" */ '../pages/settings/Project'))

const OperationsHubLayout = lazy(() => import(/* webpackChunkName: "operationshub" */ '../pages/operationshub/Layout'))
const OperationsHubProxy = lazy(() => import(/* webpackChunkName: "operationshub" */ '../pages/operationshub/Proxy'))
const OperationsHubSections = lazy(() => import(/* webpackChunkName: "operationshub" */ '../pages/operationshub/Sections'))
const OperationsHubSection = lazy(() => import(/* webpackChunkName: "operationshub" */ '../pages/operationshub/Section'))
const OperationsHubArticle = lazy(() => import(/* webpackChunkName: "operationshub" */ '../pages/operationshub/Article'))
const OperationsHubPage = lazy(() => import(/* webpackChunkName: "operationshub" */ '../pages/operationshub/Page'))

const SupportLayout = lazy(() => import(/* webpackChunkName: "support" */ '../pages/support/Layout'))
const SupportThreads = lazy(() => import(/* webpackChunkName: "support" */ '../pages/support/Threads'))

const CoconutPpcPaymentsRedirect = lazy(() => import(/* webpackChunkName: "coconut_ppc_payments" */ '../pages/coconut_ppc_payments/Redirect'))

const ReportingLayout = lazy(() => import(/* webpackChunkName: "reporting" */ '../pages/reporting/Layout'))
const ReportingDashboard = lazy(() => import(/* webpackChunkName: "reporting" */ '../pages/reporting/Dashboard'))

const AstonishLayout = lazy(() => import(/* webpackChunkName: "astonish" */ '../pages/astonish/Layout'))
const AstonishDashboard = lazy(() => import(/* webpackChunkName: "astonish" */ '../pages/astonish/Dashboard'))
const AstonishCampaigns = lazy(() => import(/* webpackChunkName: "astonish" */ '../pages/astonish/Campaigns'))
const AstonishTemplates = lazy(() => import(/* webpackChunkName: "astonish" */ '../pages/astonish/Templates'))
const AstonishEditTemplate = lazy(() => import(/* webpackChunkName: "astonish" */ '../pages/astonish/EditTemplate'))

const MarketingLayout = lazy(() => import(/* webpackChunkName: "marketing" */ '../pages/marketing/Layout'))
const MarketingCampaigns = lazy(() => import(/* webpackChunkName: "marketing" */ '../pages/marketing/Campaigns'))
const MarketingPosts = lazy(() => import(/* webpackChunkName: "marketing" */ '../pages/marketing/Posts'))
const MarketingCalendar = lazy(() => import(/* webpackChunkName: "marketing" */ '../pages/marketing/Calendar'))
const MarketingSocials = lazy(() => import(/* webpackChunkName: "marketing" */ '../pages/marketing/Socials'))
const MarketingSettingsLayout = lazy(() => import(/* webpackChunkName: "marketing" */ '../pages/marketing/SettingsLayout'))
const MarketingSettingsProfileAdmin = lazy(() => import(/* webpackChunkName: "marketing" */ '../pages/marketing/SettingsProfileAdmin'))
const MarketingSettingsProfileForm = lazy(() => import(/* webpackChunkName: "marketing" */ '../pages/marketing/SettingsProfileForm'))
const MarketingSettingsSchedule = lazy(() => import(/* webpackChunkName: "marketing" */ '../pages/marketing/SettingsSchedule'))
const MarketingSettingsPrompts = lazy(() => import(/* webpackChunkName: "marketing" */ '../pages/marketing/SettingsPrompts'))

const RBACLayout = lazy(() => import(/* webpackChunkName: "rbac" */ '../pages/rbac/Layout'))
const RBACFranchises = lazy(() => import(/* webpackChunkName: "rbac" */ '../pages/rbac/Franchises'))
const RBACUsers = lazy(() => import(/* webpackChunkName: "rbac" */ '../pages/rbac/Users'))
const RBACAdmin = lazy(() => import(/* webpackChunkName: "rbac" */ '../pages/rbac/ProfileAdmin'))

const AuditLayout = lazy(() => import(/* webpackChunkName: "audit" */ '../pages/audit/Layout'))
const AuditEvents = lazy(() => import(/* webpackChunkName: "audit" */ '../pages/audit/Events'))

const CRMLayout = lazy(() => import(/* webpackChunkName: "crm" */ '../pages/crm/Layout'))
const CRMTags = lazy(() => import(/* webpackChunkName: "crm" */ '../pages/crm/Tags'))
const CRMContacts = lazy(() => import(/* webpackChunkName: "crm" */ '../pages/crm/Contacts'))
const CRMContact = lazy(() => import(/* webpackChunkName: "crm" */ '../pages/crm/Contact'))
const CRMEvents = lazy(() => import(/* webpackChunkName: "crm" */ '../pages/crm/Events'))
const CRMImports = lazy(() => import(/* webpackChunkName: "crm" */ '../pages/crm/Imports'))

const FilestoreLayout = lazy(() => import(/* webpackChunkName: "filestore" */ '../pages/filestore/Layout'))
const FilestoreSection = lazy(() => import(/* webpackChunkName: "filestore" */ '../pages/filestore/Section'))

const NopressLayout = lazy(() => import(/* webpackChunkName: "nopress" */ '../pages/nopress/Layout'))
const NopressPage = lazy(() => import(/* webpackChunkName: "nopress" */ '../pages/nopress/Page'))

const BookingLayout = lazy(() => import(/* webpackChunkName: "booking" */ '../pages/booking/Layout'))
const BookingCalendar = lazy(() => import(/* webpackChunkName: "booking" */ '../pages/booking/Calendar'))
const BookingBookings = lazy(() => import(/* webpackChunkName: "booking" */ '../pages/booking/Bookings'))
const BookingAvailability = lazy(() => import(/* webpackChunkName: "booking" */ '../pages/booking/Availability'))
const BookingPricing = lazy(() => import(/* webpackChunkName: "booking" */ '../pages/booking/Pricing'))
const BookingReporting = lazy(() => import(/* webpackChunkName: "booking" */ '../pages/booking/Reporting'))
const BookingConfig = lazy(() => import(/* webpackChunkName: "booking" */ '../pages/booking/GeneralConfig'))

import {
  ASTONISH_CAMPAIGN_MODE_CAMPAIGN,
  ASTONISH_CAMPAIGN_MODE_EMAIL,
} from '@kaidam/fc-shared/src/constants'

const Router: FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const route = useSelector(routerSelectors.route)

  const loading = (
    <Container
      maxWidth="sm"
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Loading /> 
      </Box>
    </Container>
  )

  if(!route) {
    return loading
  }

  return (
    <RouteContext.Provider value={ route.name }>
      <Layout>
        <Route segment="notfound" exact>
          <NotFound />
        </Route>
        <Route segment="home" exact>
          <Login />
        </Route>
        <Route segment="login" exact>
          <Login />
        </Route>
        <Route segment="register" exact>
          <Register />
        </Route>
        <Route segment="logout" exact>
          <Logout />
        </Route>
        <Route segment="reset_password" exact>
          <ResetPassword />
        </Route>
        <Route segment="dashboard" exact>
          <Dashboard />
        </Route>
        <Route segment="settings">
          <Suspense>
            <SettingsLayout>
              <Route segment="password" exact>
                <SettingsPassword />
              </Route>
              <Route segment="project" exact>
                <SettingsProject />
              </Route>
            </SettingsLayout>
          </Suspense>
        </Route>
        <Route segment="operationshub">
          <Suspense>
            <OperationsHubLayout>
              <Route segment="sections" exact>
                <OperationsHubSections />
              </Route>
              <Route segment="section" exact>
                <OperationsHubSection />
              </Route>
              <Route segment="proxy" exact>
                <OperationsHubProxy />
              </Route>
              <Route segment="section">
                <Route segment="article" exact>
                  <OperationsHubArticle />
                </Route>
                <Route segment="article">
                  <Route segment="page" exact>
                    <OperationsHubPage />
                  </Route>
                </Route>
              </Route>
            </OperationsHubLayout>
          </Suspense>
        </Route>
        <Route segment="support">
          <Suspense>
            <SupportLayout>
              <Route segment="list" exact>
                <SupportThreads />
              </Route>
              <Route segment="thread" exact>
                <SupportThreads />
              </Route>
            </SupportLayout>
          </Suspense>
        </Route>
        <Route segment="coconut_ppc_payments">
          <Suspense>
            <CoconutPpcPaymentsRedirect />
          </Suspense>
        </Route>
        <Route segment="reporting">
          <Suspense>
            <ReportingLayout>
              <Route segment="org" exact>
                <ReportingDashboard />
              </Route>
            </ReportingLayout>
          </Suspense>
        </Route>
        <Route segment="astonish">
          <Suspense>
            <AstonishLayout>
              <Route segment="dashboard">
                <AstonishDashboard />
              </Route>
              <Route segment="campaigns">
                <AstonishCampaigns
                  title="Campaigns"
                  mode={ ASTONISH_CAMPAIGN_MODE_CAMPAIGN }
                />
              </Route>
              <Route segment="emails">
                <AstonishCampaigns
                  title="Emails"
                  mode={ ASTONISH_CAMPAIGN_MODE_EMAIL }
                />
              </Route>
              <Route segment="templates">
                <AstonishTemplates />
              </Route>
              <Route segment="edit_template">
                <AstonishEditTemplate />
              </Route>
            </AstonishLayout>
          </Suspense>
        </Route>
        <Route segment="filestore">
          <Suspense>
            <FilestoreLayout>
              <Route segment="section">
                <FilestoreSection />
              </Route>
            </FilestoreLayout>
          </Suspense>
        </Route>
        <Route segment="network">
          <Suspense>
            <RBACLayout>
              <Route segment="franchises">
                <Route segment="list" exact>
                  <RBACFranchises />
                </Route>
              </Route>
              <Route segment="users">
                <Route segment="list" exact>
                  <RBACUsers />
                </Route>
              </Route>
              <Route segment="admin" exact>
                <RBACAdmin />
              </Route>
            </RBACLayout>
          </Suspense>
        </Route>
        <Route segment="audit">
          <Suspense>
            <AuditLayout>
              <Route segment="events" exact>
                <AuditEvents />
              </Route>
            </AuditLayout>
          </Suspense>
        </Route>
        <Route segment="marketing">
          <Suspense>
            <MarketingLayout>
              <Route segment="campaigns" exact>
                <MarketingCampaigns />
              </Route>
              <Route segment="posts" exact>
                <MarketingPosts />
              </Route>
              <Route segment="calendar" exact>
                <MarketingCalendar />
              </Route>
              <Route segment="socials" exact>
                <MarketingSocials />
              </Route>
              <Route segment="settings">
                <MarketingSettingsLayout>
                  <Route segment="profile_admin" exact>
                    <MarketingSettingsProfileAdmin />
                  </Route>
                  <Route segment="profile_form" exact>
                    <MarketingSettingsProfileForm />
                  </Route>
                  <Route segment="schedule" exact>
                    <MarketingSettingsSchedule />
                  </Route>
                  <Route segment="prompts" exact>
                    <MarketingSettingsPrompts />
                  </Route>
                </MarketingSettingsLayout>
              </Route>
            </MarketingLayout>
          </Suspense>
        </Route>
        <Route segment="crm">
          <Suspense>
            <CRMLayout>
              <Route segment="tags">
                <CRMTags />
              </Route>
              <Route segment="contacts" exact>
                <CRMContacts />
              </Route>
              <Route segment="contact" exact>
                <CRMContact />
              </Route>
              <Route segment="events">
                <CRMEvents />
              </Route>
              <Route segment="imports">
                <CRMImports />
              </Route>
            </CRMLayout>
          </Suspense>
        </Route>
        <Route segment="nopress">
          <Suspense>
            <NopressLayout>
              <Route segment="org.website.page">
                <NopressPage />
              </Route>
            </NopressLayout>
          </Suspense>
        </Route>
        <Route segment="booking">
          <Suspense>
            <BookingLayout>
              <Route segment="calendar">
                <BookingCalendar />
              </Route>
              <Route segment="bookings">
                <BookingBookings />
              </Route>
              <Route segment="availability">
                <BookingAvailability />
              </Route>
              <Route segment="pricing">
                <BookingPricing />
              </Route>
              <Route segment="reporting">
                <BookingReporting />
              </Route>
              <Route segment="config">
                <BookingConfig />
              </Route>
            </BookingLayout>
          </Suspense>
        </Route>
        {
          children
        }
      </Layout>
    </RouteContext.Provider>
  )
}

export default Router